import React from 'react';
import {
  Container,
  Typography,
  Grid,
  CardContent,
  Box,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Security as SecurityIcon,
  Settings as SystemIcon,
  Business as CompanyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../../contexts/PermissionContext';
import { GlassBackdrop, GlassSurface } from '../../../components/GlassDashboard';

interface ConfigModule {
  id: string;
  title: string;
  description: string;
  icon: React.ReactElement;
  route: string;
  available: boolean;
  color: string;
  status?: string;
  miniModuleName?: string | null;
}

const ConfigurationOverview: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const configModules: ConfigModule[] = [
    {
      id: 'security',
      title: 'Sécurité',
      description: 'Configuration de la sécurité et authentification',
      icon: <SecurityIcon />,
      route: '/configuration/security',
      available: false,
      color: '#F44336',
      status: 'Bientôt',
      miniModuleName: null,
    },
    {
      id: 'system',
      title: 'Paramètres Système',
      description: 'Configuration générale du système',
      icon: <SystemIcon />,
      route: '/configuration/system',
      available: false,
      color: '#607D8B',
      status: 'Bientôt',
      miniModuleName: null,
    },
    {
      id: 'company',
      title: 'Informations Entreprise',
      description: 'Données de l\'entreprise et coordonnées',
      icon: <CompanyIcon />,
      route: '/configuration/company',
      available: false,
      color: '#795548',
      status: 'Bientôt',
      miniModuleName: null,
    },
  ];

  // Check if user has permission for a module
  const hasModulePermission = (module: ConfigModule) => {
    // Coming soon items are always disabled
    if (!module.available) return false;

    // Check permissions if miniModuleName is specified
    if (module.miniModuleName) {
      return hasPermission('Config', 'view', module.miniModuleName);
    }

    return true;
  };

  // Map modules with access status
  const visibleModules = configModules.map((module) => ({
    ...module,
    hasAccess: hasModulePermission(module),
  }));

  const handleModuleClick = (module: ConfigModule & { hasAccess: boolean }) => {
    if (module.hasAccess) {
      navigate(module.route);
    }
  };

  return (
    <Container maxWidth="xl" disableGutters sx={{ px: 0 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Configuration Globale
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gérez les paramètres globaux du système : sécurité, paramètres et informations entreprise
        </Typography>
      </Box>

      <GlassBackdrop>
        <Grid container spacing={3}>
          {visibleModules.map((module) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={module.id}>
              <GlassSurface
                accentColor={module.hasAccess ? module.color : undefined}
                interactive={module.hasAccess}
                onClick={module.hasAccess ? () => handleModuleClick(module) : undefined}
                sx={{ p: 2, opacity: module.hasAccess ? 1 : 0.7 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    height: '100%',
                    minHeight: 200,
                    position: 'relative',
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: module.hasAccess ? module.color : 'grey.400',
                      width: 56,
                      height: 56,
                      mb: 2,
                    }}
                  >
                    {React.cloneElement(module.icon, {
                      sx: { fontSize: 28, color: 'white' }
                    })}
                  </Avatar>

                  <CardContent sx={{ textAlign: 'center', pb: 2 }}>
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="h3"
                      color={module.hasAccess ? 'text.primary' : 'text.disabled'}
                      sx={{ fontSize: '1rem', fontWeight: 600 }}
                    >
                      {module.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={module.hasAccess ? 'text.secondary' : 'text.disabled'}
                      sx={{ minHeight: 50, fontSize: '0.875rem' }}
                    >
                      {module.description}
                    </Typography>
                    {module.available && !module.hasAccess && (
                      <Typography
                        variant="caption"
                        color="error.main"
                        sx={{ fontStyle: 'italic', display: 'block', mt: 1 }}
                      >
                        Accès non autorisé
                      </Typography>
                    )}
                  </CardContent>

                  <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                    <Chip
                      label={module.hasAccess ? module.status : (!module.available ? module.status : 'Refusé')}
                      size="small"
                      color={module.hasAccess ? 'success' : (!module.available ? 'warning' : 'error')}
                      variant={module.hasAccess ? 'filled' : 'outlined'}
                      sx={{ fontSize: '0.7rem' }}
                    />
                  </Box>
                </Box>
              </GlassSurface>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 4 }}>
          <GlassSurface interactive={false} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Configuration Globale
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Ce module vous permet de configurer les paramètres globaux de votre système :
              sécurité, paramètres système et informations entreprise.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Les configurations spécifiques à chaque domaine (articles, fournisseurs, clients, etc.)
              sont accessibles depuis leurs modules respectifs.
            </Typography>
          </GlassSurface>
        </Box>
      </GlassBackdrop>
    </Container>
  );
};

export default ConfigurationOverview;
